import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle, Mail, ArrowRight } from "lucide-react";

import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { fetchOrderBySession } from "../lib/api";
import { useCart } from "../context/CartContext";

const fmtEur = (val) => `€ ${Number(val || 0).toFixed(2)}`;

export default function WebshopSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ stage: "checking", order: null });
  const attempts = useRef(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ stage: "missing", order: null });
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const order = await fetchOrderBySession(sessionId);
        if (!cleared.current) {
          // Cart can be cleared as soon as we have a matching order draft
          clear();
          cleared.current = true;
        }
        if (order.status === "paid" || order.payment_status === "paid") {
          setState({ stage: "paid", order });
          return;
        }
        if (order.status === "failed" || order.status === "cancelled") {
          setState({ stage: "failed", order });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          // Order exists but webhook might be slow — show a "processing" state
          setState({ stage: "processing", order });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        attempts.current += 1;
        if (attempts.current >= 5) {
          setState({ stage: "error", order: null });
          return;
        }
        timer = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const order = state.order;

  return (
    <>
      <SEO
        title="Betaling ontvangen | 5999Cargo Webshop"
        description="Bedankt voor uw bestelling bij 5999Cargo. U ontvangt een bevestiging per e-mail."
      />
      <section className="min-h-[70vh] bg-white py-24" data-testid="webshop-success-page">
        <div className="max-w-2xl mx-auto px-4 text-center">
          {state.stage === "checking" && (
            <>
              <Clock className="w-14 h-14 text-[#1a5193] mx-auto animate-pulse" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                Betalingsstatus controleren…
              </h1>
              <p className="text-gray-600 mt-3">
                Een ogenblik geduld. We checken uw betaling bij Stripe.
              </p>
            </>
          )}
          {(state.stage === "paid" || state.stage === "processing") && (
            <>
              <CheckCircle2
                className={`w-16 h-16 mx-auto ${state.stage === "paid" ? "text-green-600" : "text-[#1a5193]"}`}
                data-testid="webshop-success-icon"
              />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                {state.stage === "paid" ? "Betaling ontvangen" : "Betaling wordt verwerkt"}
              </h1>
              <p className="text-gray-600 mt-3">
                Bedankt voor uw bestelling. Uw betaling{" "}
                {state.stage === "paid" ? "is ontvangen" : "wordt verwerkt"} en uw bestelling wordt afgehandeld.
                U ontvangt een bevestiging per e-mail zodra de verwerking is afgerond.
              </p>

              {order && (
                <div className="mt-8 inline-block text-left bg-gray-50 border border-gray-200 px-6 py-5 min-w-[320px]" data-testid="success-order-summary">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">Ordernummer</div>
                  <div className="font-mono text-sm break-all" data-testid="success-order-id">{order.id}</div>
                  <div className="mt-3 text-xs uppercase tracking-wider text-gray-500 font-bold">Totaal</div>
                  <div className="font-chivo font-black text-xl text-[#082c50]" data-testid="success-order-total">
                    {fmtEur(order.amount_total)}
                  </div>
                  {order.customer?.email && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <Mail className="w-4 h-4" />
                      <span data-testid="success-order-email">{order.customer.email}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Button
                  asChild
                  className="bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs"
                >
                  <Link to="/webshop" data-testid="success-back-shop">Verder winkelen</Link>
                </Button>
                  <a href="tel:+31488235999" data-testid="success-contact-link" className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-[#082c50] hover:bg-gray-50 rounded-xl font-chivo font-bold uppercase text-xs">
                    Bel +31 (0) 488 23 5999 <ArrowRight className="ml-2 w-3 h-3 inline" />
                  </a>
              </div>
            </>
          )}
          {state.stage === "failed" && (
            <>
              <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                Betaling niet voltooid
              </h1>
              <p className="text-gray-600 mt-3">
                Uw betaling is niet voltooid. U kunt teruggaan naar de webshop en opnieuw proberen.
              </p>
              <div className="mt-8">
                <Button asChild className="bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs">
                  <Link to="/webshop" data-testid="success-failed-shop">Terug naar webshop</Link>
                </Button>
              </div>
            </>
          )}
          {(state.stage === "missing" || state.stage === "error") && (
            <>
              <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto" />
              <h1 className="font-chivo font-black text-3xl sm:text-4xl text-[#082c50] mt-6">
                Bestelling niet gevonden
              </h1>
              <p className="text-gray-600 mt-3">
                We konden de bestelling niet ophalen. Mocht uw betaling geslaagd zijn, dan ontvangt u nog steeds
                een bevestiging per e-mail. Neem bij twijfel contact met ons op.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Button asChild className="bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs">
                  <Link to="/webshop" data-testid="success-error-shop">Terug naar webshop</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl font-chivo font-bold uppercase text-xs">
                  <a href="tel:+31488235999" data-testid="success-error-contact">Bel +31 (0) 488 23 5999</a>
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
